import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, ElementRef } from '@angular/core';
import { KuiModalRefComponent } from 'app/key-ui/modal/modal-ref.component';
import { IKuiModalAction } from 'app/key-ui';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observer, Observable, Subscription } from 'rxjs';
import { AdminEditorService } from '../admin.service';
import { share } from 'rxjs/operators';
import { UserTaskService } from 'app/services';
import { DeviceResponse } from '@key-telematics/fleet-api-client';
import { getDeviceConfiguration } from 'app/services/api/api.utils';


@Component({
    templateUrl: 'console.component.html',
    styleUrls: ['console.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [UserTaskService],
    standalone: false
})
export class AdminConsoleModalComponent extends KuiModalRefComponent<{ deviceId: string }> implements OnInit, OnDestroy {

    constructor(
        private admin: AdminEditorService,
        private tasks: UserTaskService,
        private i18n: TranslateService,
        private ref: ChangeDetectorRef,
        private el: ElementRef
    ) {
        super();
    }

    lines: string[] = [];

    modalActions: IKuiModalAction[];

    ws: WebSocket;
    connection: Subject<any>;
    sub: Subscription;

    text: string;
    device: DeviceResponse;

    taskIds: string[] = [];
    taskFeedSub: Subscription;

    ngOnInit() {
        this.modalActions = [{
            text: this.i18n.instant('DIALOG.CLOSE'),
            style: 'primary',
            keypress: 27,
            action: () => {
                this.close();
                this.actions.close();
            },
        }].filter(x => x);

        this.admin.getCached(`getDevice/${this.data.deviceId}`, api => api.entities.getDevice(this.data.deviceId), 0).then(async device => {
            this.device = device;
            if (device?.asset?.id) {
            this.taskFeedSub = this.tasks.getTaskFeed(device.asset.id).subscribe(items => {
                items.forEach(item => {
                    this.write(`${item.title}: ${item.state}`);
                });
            });
        }

            const config = await this.admin.getCached(`getDeviceConfiguration/${device.id}`, api => getDeviceConfiguration(api, device));
            if (config.features.upload_settings && device.asset) {
                this.modalActions = [{
                    text: this.i18n.instant('ADMIN.EDITORS.DEVICE.CONSOLE.ACTIONS.UPLOAD_SETTINGS'),
                    style: 'secondary',
                    icon: 'upload',
                    action: () => {
                        this.uploadSettings();
                        return true; // don't close the modal
                    },
                },
                ...this.modalActions,
                ];
            }

            if (config.features.console) {
                this.connect();
            }

            this.ref.markForCheck();

        });


    }

    ngOnDestroy() {
        if (this.taskFeedSub) {
            this.taskFeedSub.unsubscribe();
        }
        this.close();
    }

    detachModal() {
        this.actions.close();
    }

    write(line: string) {
        const el = this.el.nativeElement.querySelector('.console');
        const diff = el ? el.scrollHeight - (el.scrollTop + el.clientHeight) : 0;

        this.lines.push(line);
        if (this.lines.length > 500) {
            this.lines.shift();
        }
        this.ref.markForCheck();

        // if the user was near the bottom, scroll automatically
        setTimeout(() => {
            if (el && diff < 30) {
                el.scrollTop = el.scrollHeight;
            }
        });
    }

    createWebSocket(url: string): Subject<any> {
        this.ws = new WebSocket(url);
        const observable = new Observable(
            (obs: Observer<Event>) => {
                this.ws.onmessage = obs.next.bind(obs);
                this.ws.onerror = obs.error.bind(obs);
                this.ws.onclose = obs.complete.bind(obs);
                return this.ws.close.bind(this.ws);
            }).pipe(share());

        const observer = {
            next: (data: Object) => {
                if (this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify(data));
                } else {
                    this.write('[ERROR] Not connected'); // not translating this, all console output is in english (it's from the backend)
                }
            },
        };
        return Subject.create(observer, observable);
    }

    connect() {
        this.close();
        try {
            // The console traffic currently goes directly to the device-service (previous, directly to the devices-websocket-proxy)
            // and therefore we need to manipulate the api url. The localhost replacement is needed to ensure this works locally
            // We have plans to put this behind the api, which will require the ui to create a websocket connection to the api which will
            // proxy the connection to the device-service, but until then, this will have to stay
            const host = new URL(this.admin.apiEndpoint.replace('localhost:8080', 'localhost:11322')).host.replace('api.', 'dws.');
            const protocol = host.indexOf('localhost:') >= 0 ? 'ws' : 'wss';
            const url = `${protocol}://${host}/console?wsport=80`;
            this.write(`Opening connection to ${url}`); // not translating this
            this.connection = this.createWebSocket(url);
            this.sub = this.connection.subscribe(event => {
                try {
                    if (event.type === 'error') {
                        this.write('[ERROR] Not connected'); // not translating this
                    }
                    if (event.type === 'message') {
                        this.write(event.data);
                        if (event.data === 'PROXY CONNECTED') {
                            this.connection.next({
                                action: 'connect',
                                data: {
                                    deviceId: this.data.deviceId,
                                    userId: this.admin.user.id,
                                },
                            });
                        }
                    }
                } catch (err) {
                    this.write(`[ERROR] ${err.message}`);
                }
            }, error => {
                this.write(`[ERROR] ${error.message || 'Console access is not available for this device type'}`);
            });
        } catch (err) {
            this.write(`[ERROR] ${err.message}`);
        }

    }

    close() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
            this.sub.unsubscribe();
            this.sub = null;
            this.connection = null;
        }
    }

    onSend(_event) {
        this.connection.next({
            action: 'command',
            data: {
                command: this.text,
            },
        });
        this.text = '';
    }

    async uploadSettings() {
        try {
            this.write('Queueing a settings upload request...');
            const accessories = Object.entries(this.device.accessories).map(([key, accessory]) => {
                return {
                    id: key,
                    ...accessory
                };
            });
            const task = await this.admin.applyUpdate(api => api.tasks.createTask({
                ownerId: this.device.owner.id,
                options: {
                    retries: 3,
                    delay: 1,
                    timeout: 5,
                },
                type: 'DeviceUploadTask',
                data: {
                    asset: {
                        id: this.device.asset.id,
                        name: this.device.asset.name,
                    },
                    settings: this.device.settings,
                    accessories: accessories
                },
            }));
            this.taskIds.push(task.id);
            return task;
        } catch (err) {
            this.write(`[ERROR] ${err.message}`);
        }

    }

}
